/**
 * Visual grouping for CSML node types.
 *
 * Every node type belongs to exactly one category, and that category drives
 * both the palette grouping and the colour language on the canvas - so a card
 * and the palette entry that produced it always read as the same thing. The
 * class strings are spelled out in full rather than composed at runtime,
 * because Tailwind only ships classes it can find as literals in the source.
 */

export const NODE_CATEGORY_IDS = [
  'start',
  'message',
  'ask',
  'logic',
  'integration',
];

export const NODE_CATEGORY_BY_TYPE = {
  start: 'start',

  say: 'message',
  typing: 'message',
  image: 'message',
  video: 'message',
  audio: 'message',
  file: 'message',
  url: 'message',
  latex: 'message',
  carousel: 'message',

  question: 'ask',
  quickreply: 'ask',
  radio: 'ask',
  multiselect: 'ask',
  checkbox: 'ask',
  dropdown: 'ask',
  input: 'ask',
  calendar: 'ask',
  signature: 'ask',
  hold: 'ask',

  userinput: 'logic',
  router: 'logic',
  workinghours: 'logic',

  api: 'integration',
  assign: 'integration',
  oauth: 'integration',
};

export const CATEGORY_STYLES = {
  start: {
    accent: 'bg-n-teal-9',
    header: 'bg-n-teal-3 border-n-teal-6',
    chip: 'bg-n-teal-9 text-white',
    port: 'border-n-teal-9',
    portDot: 'bg-n-teal-9',
    paletteIcon: 'bg-n-teal-3 text-n-teal-11',
    stroke: 'rgb(var(--teal-9))',
  },
  message: {
    accent: 'bg-n-blue-9',
    header: 'bg-n-blue-3 border-n-blue-6',
    chip: 'bg-n-blue-9 text-white',
    port: 'border-n-blue-9',
    portDot: 'bg-n-blue-9',
    paletteIcon: 'bg-n-blue-3 text-n-blue-11',
    stroke: 'rgb(var(--blue-9))',
  },
  ask: {
    accent: 'bg-n-violet-9',
    header: 'bg-n-violet-3 border-n-violet-6',
    chip: 'bg-n-violet-9 text-white',
    port: 'border-n-violet-9',
    portDot: 'bg-n-violet-9',
    paletteIcon: 'bg-n-violet-3 text-n-violet-11',
    stroke: 'rgb(var(--violet-9))',
  },
  logic: {
    accent: 'bg-n-amber-9',
    header: 'bg-n-amber-3 border-n-amber-6',
    chip: 'bg-n-amber-9 text-n-slate-12',
    port: 'border-n-amber-11',
    portDot: 'bg-n-amber-11',
    paletteIcon: 'bg-n-amber-3 text-n-amber-11',
    stroke: 'rgb(var(--amber-11))',
  },
  integration: {
    accent: 'bg-n-iris-9',
    header: 'bg-n-iris-3 border-n-iris-6',
    chip: 'bg-n-iris-9 text-white',
    port: 'border-n-iris-9',
    portDot: 'bg-n-iris-9',
    paletteIcon: 'bg-n-iris-3 text-n-iris-11',
    stroke: 'rgb(var(--iris-9))',
  },
};

// Icon for the kind of branch an out-port represents, shown in front of its
// label so a fork reads at a glance (button vs keyword vs working hours).
export const PORT_ICONS = {
  button: 'i-lucide-mouse-pointer-click',
  reply: 'i-lucide-reply',
  option: 'i-lucide-list',
  cardbtn: 'i-lucide-gallery-horizontal',
  card: 'i-lucide-gallery-horizontal',
  open: 'i-lucide-door-open',
  closed: 'i-lucide-door-closed',
  keyword: 'i-lucide-search',
  rule: 'i-lucide-filter',
  default: 'i-lucide-arrow-right',
};

export function getPortIcon(type) {
  return PORT_ICONS[type] || 'i-lucide-git-branch';
}

export function getNodeCategory(type) {
  return NODE_CATEGORY_BY_TYPE[type] || 'message';
}

export function getCategoryStyle(type) {
  return CATEGORY_STYLES[getNodeCategory(type)];
}
